import { ReactComponent as LetterSpacingIcon } from 'assets/icons/letter_spacing.svg';
import { SliderPopover } from 'modules/common/components/ToolbarPopover';
import { MutableRefObject, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor as useInfographEditor, useWidget } from 'widgets/sdk';
import { useResponsiveGroup } from 'widgets/sdk';

import { MAX_LETTER_SPACING, MIN_LETTER_SPACING } from '../TextWidget.config';
import { formatLetterSpacing } from '../TextWidget.helpers';
import { TextWidgetData } from '../TextWidget.types';

type LetterSpacingProps = {
  isTypingRef: MutableRefObject<boolean>;
  textWrapperRef: MutableRefObject<HTMLDivElement | null>;
};
export const LetterSpacing = memo(({ isTypingRef, textWrapperRef }: LetterSpacingProps) => {
  const { letterSpacing = 0, updateWidget, widgetId } = useWidget<TextWidgetData>();
  const { boundingBox } = useInfographEditor();
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { responsiveGroupResize } = useResponsiveGroup(widgetId);

  const title = t('letterSpacing.title');

  const onChangeLetterSpacing = (newLetterSpacing: number) => {
    if (!textWrapperRef.current) return;

    isTypingRef.current = false;

    // measure the height before & after setting the new letter spacing
    const previousHeight = textWrapperRef.current.clientHeight;
    textWrapperRef.current.style.setProperty('letter-spacing', formatLetterSpacing(newLetterSpacing));
    const heightPx = textWrapperRef.current.clientHeight;

    // only for text widgets in groups - the height of the widget can be updated by the letter spacing -> update group member widgets
    responsiveGroupResize({ height: heightPx, previousHeight });

    // update the widget
    updateWidget({ letterSpacing: newLetterSpacing });

    // update the bounding box
    setTimeout(() => boundingBox.updateRect(), 0);
  };

  return (
    <SliderPopover
      value={letterSpacing}
      icon={<LetterSpacingIcon />}
      label={title}
      onChange={onChangeLetterSpacing}
      max={MAX_LETTER_SPACING}
      min={MIN_LETTER_SPACING}
      title={title}
      suffix={'%'}
      activeIconStyle={{ '& path': { fill: 'upgrade.blue.700' } }}
    />
  );
});
LetterSpacing.displayName = 'LetterSpacing';
